import { sql } from 'drizzle-orm';
import {
  type AnyPgColumn,
  index,
  integer,
  pgTable,
  text,
  timestamp,
  uniqueIndex,
  uuid,
} from 'drizzle-orm/pg-core';
import { userRoleEnum, userStatusEnum } from './enums.ts';

/**
 * Tabela `users` — espelho local do Clerk com role + status de acesso.
 *
 * NÃO existe fluxo de aprovação de usuário (decisão de produto): quem passa
 * pelo SSO corporativo já é funcionário, então cada usuário nasce
 * `status='approved'`. A barreira real de entrada são o SSO e a MATRÍCULA
 * validada contra o espelho do RH (gate `NEEDS_ONBOARDING`).
 *
 * Fluxo (`apps/api/src/middleware/auth.ts`):
 *  - Primeiro usuário a logar (count=0) e o `SUPER_ADMIN_EMAIL` são criados com
 *    `role='admin'`; os demais com `role='user'`.
 *  - Todos, sem exceção, são criados com `status='approved'` + `approvedAt`
 *    preenchido no ato — o `.default('pending')` da coluna abaixo nunca é usado
 *    pela API, que passa o status explicitamente. `approvedById` fica `null`
 *    porque não há aprovador humano.
 *  - Sem matrícula, o usuário aprovado só enxerga `GET /api/me` e
 *    `POST /api/me/onboarding` (onde confirma a matrícula contra
 *    `totvs_collaborators`).
 *
 * `pending` e `rejected` continuam no enum de propósito, para marcação MANUAL
 * no banco: um `UPDATE` para `rejected` revoga o acesso sem apagar o histórico
 * (o `blockUnapproved` do middleware segue ativo e devolve 403). Não existe
 * endpoint nem tela de aprovação — nenhum usuário chega a esses status sozinho.
 *
 * `clerkUserId` é a fonte da verdade — match com o `userId` do `getAuth(c)`.
 */
export const users = pgTable(
  'users',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    clerkUserId: text('clerk_user_id').notNull().unique(),
    email: text('email').notNull(),
    firstName: text('first_name'),
    lastName: text('last_name'),
    imageUrl: text('image_url'),
    role: userRoleEnum('role').notNull().default('user'),
    status: userStatusEnum('status').notNull().default('pending'),
    approvedById: uuid('approved_by_id').references((): AnyPgColumn => users.id, {
      onDelete: 'set null',
    }),
    approvedAt: timestamp('approved_at', { withTimezone: true }),
    // Vínculo TOTVS (onboarding por matrícula) — preenchidos no primeiro login
    // via POST /api/me/onboarding a partir do espelho totvs_collaborators.
    matricula: text('matricula'),
    funcao: text('funcao'),
    totvsFilialCode: text('totvs_filial_code'),
    setorNome: text('setor_nome'),
    // ── Conciliação com o RH ─────────────────────────────────────────────────
    // Estes quatro campos existem porque os de cima são um RETRATO do onboarding
    // e ninguém os atualizava: quem trocasse de filial mantinha o acesso da
    // antiga para sempre, e quem fosse demitido continuava com o retrato válido.
    //
    // `rhSituacao` guarda o CODSITUACAO visto na ÚLTIMA conferência. Não vem do
    // espelho `totvs_collaborators`: o espelho só traz 'A' e 'F', e demitido
    // (13.378 registros na origem, contra 3.827 ativos) nunca chega lá. Vem de
    // consulta DIRIGIDA às matrículas que têm conta.
    rhSituacao: text('rh_situacao'),
    /** Quando a conta foi conferida contra o RH pela última vez. */
    rhCheckedAt: timestamp('rh_checked_at', { withTimezone: true }),
    /** Quando o acesso caiu. Null enquanto a conta está ativa. */
    deactivatedAt: timestamp('deactivated_at', { withTimezone: true }),
    /**
     * POR QUE caiu, em código estável para a tela distinguir os casos:
     * `demitido_rh` (CODSITUACAO 'D'), `sem_matricula` (conta que nunca vinculou
     * matrícula) ou texto livre quando um admin desativa à mão.
     */
    deactivationReason: text('deactivation_reason'),
    // Anti força-bruta do onboarding: contador de erros consecutivos + bloqueio
    // exponencial (30s dobrando a cada 5 erros, teto 15min).
    onboardingFailedAttempts: integer('onboarding_failed_attempts').notNull().default(0),
    onboardingLockedUntil: timestamp('onboarding_locked_until', { withTimezone: true }),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    index('users_clerk_user_id_idx').on(t.clerkUserId),
    index('users_email_idx').on(t.email),
    index('users_status_idx').on(t.status),
    // Matrícula é vínculo ÚNICO entre contas — índice parcial para permitir
    // muitos NULLs (quem ainda não fez onboarding).
    uniqueIndex('users_matricula_uq').on(t.matricula).where(sql`matricula is not null`),
  ],
);

export type User = typeof users.$inferSelect;
export type NewUser = typeof users.$inferInsert;
